import { useState } from "react";
import { api } from "../lib/api";

/**
 * Intel admin page — manually trigger FirmIntelCrawl at any tier for
 * all firms or a single firm. Shows the last run summary.
 */
export function IntelPage() {
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [onlySlug, setOnlySlug] = useState("");

  const run = async (tier: "pricing" | "rules" | "full") => {
    setBusy(true);
    setResult(null);
    try {
      // NOTE: this route is HMAC-verified on the Worker side — calling
      // it from the browser requires a server-side relay. For v1 this
      // page shows instructions + a copyable curl command; the actual
      // trigger lives in the seeds CLI.
      const cmd = buildCurl(tier, onlySlug || undefined);
      setResult(`# paste into a terminal with EA_WEBHOOK_SECRET set\n${cmd}`);
    } finally {
      setBusy(false);
    }
  };

  void api;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Intel crawler</h1>

      <div className="pfh-card space-y-3">
        <div className="text-sm text-muted-foreground">
          The /admin/workflows/intel/run endpoint is HMAC-verified, so triggering
          it from the browser requires knowing EA_WEBHOOK_SECRET (which the UI
          deliberately does NOT have). Use the seeds CLI instead:
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Only slug (optional)</label>
          <input
            value={onlySlug}
            onChange={(e) => setOnlySlug(e.target.value)}
            placeholder="e.g. ftmo"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => run("pricing")}
            disabled={busy}
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            pricing
          </button>
          <button
            onClick={() => run("rules")}
            disabled={busy}
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            rules
          </button>
          <button
            onClick={() => run("full")}
            disabled={busy}
            className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            full
          </button>
        </div>
        {result ? (
          <pre className="mt-2 overflow-x-auto rounded-md bg-background p-3 text-xs text-muted-foreground">
            {result}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

function buildCurl(tier: string, only?: string): string {
  const body = JSON.stringify({ tier, ...(only ? { onlySlug: only } : {}) });
  // Escape single quotes for safe embedding inside a single-quoted shell arg:
  // close the quoted string, emit an escaped quote, then reopen it.
  const shellSingleQuote = (s: string): string =>
    `'${s.replace(/'/g, "'\"'\"'")}'`;
  const safeBody = shellSingleQuote(body);
  const safeOnly = only ? shellSingleQuote(only) : "";
  return (
    `pnpm -F @pfh/seeds seed:intel:run -- --tier ${tier}` +
    (only ? ` --only ${safeOnly}` : "") +
    `\n\n# or direct curl (requires shell hmac):\n` +
    `curl -X POST https://api.pfh.trading/admin/workflows/intel/run -d ${safeBody}`
  );
}
